/**
 * lp-017. Where a customer writes down what they check by hand.
 *
 * The screen has one job beyond capture: make a weak item fail here rather than
 * silently on every document forever. An item that cannot be answered from the
 * text returns nothing every time it runs, which is indistinguishable from an
 * item that passed — so the review is not decoration, it is the only place that
 * failure is visible.
 *
 * Nothing here rewrites an item. A suggestion sits beside the words the
 * customer wrote until they take it.
 */
angular.module('LeasePilot').directive('checklistEditor', [
  'ApiService',
  '$rootScope',
  function(ApiService, $rootScope) {
    return {
      restrict: 'E',
      templateUrl: 'checklist_editor.html',
      link: function(scope, element) {
        // Delegated, the way the playbook editor's is, and for the same reason:
        // the input is created and destroyed as the screen changes, and a
        // listener bound to the element itself would be attached to whichever
        // one existed at link time.
        const root = element && element[0];
        if (root) {
          root.addEventListener('change', function(event) {
            const target = event.target;
            if (!target || !target.matches || !target.matches('[data-checklist-file]'))
              return;

            const file = (target.files || [])[0];
            // Emptied so choosing the same file again still fires a change —
            // somebody re-picking after a failed read would otherwise get
            // nothing at all and no reason why.
            target.value = '';
            scope.$apply(function() {
              scope.importDocument(file);
            });
          });
        }

        scope.loading = true;
        scope.saving = false;
        scope.reviewing = false;
        scope.error = null;
        scope.items = [];
        scope.documentTypes = [];
        scope.documentTypeLabels = {};
        scope.documentType = null;
        // `picker.documentType`, not `documentType`. The controls bar sits
        // inside an `ng-if`, so a bare `ng-model` writes the choice to that
        // child scope — and `load()` sets `loading`, which tears the child down
        // and builds a new one inheriting the parent's unchanged value, so the
        // select snaps back on every change. A dotted path resolves through the
        // prototype chain to the object the parent owns. The playbook editor
        // shipped this bug once already; this is the same fix for the same
        // reason.
        scope.picker = { documentType: null };
        scope.draft = { text: '', severity: 'medium', scopeHint: '' };

        scope.severities = ['high', 'medium', 'low'];

        // What each verdict means to the person reading it, in their terms.
        // `ok` says nothing on purpose: a screen that congratulates every good
        // item teaches people to skim the ones that are not.
        const VERDICTS = {
          unevaluable: 'This cannot be answered from the text as written.',
          outside_document:
            'This needs something the document does not contain, so it will not be checked here.',
          duplicate: 'LeasePilot already checks this.',
        };

        scope.verdictLabel = function(item) {
          return (item.review && VERDICTS[item.review.verdict]) || null;
        };

        scope.needsAttention = function(item) {
          return Boolean(scope.verdictLabel(item));
        };

        scope.attentionCount = function() {
          return scope.items.filter(scope.needsAttention).length;
        };

        function fail(error) {
          scope.error =
            (error && error.data && error.data.error) ||
            'Something went wrong. Nothing was changed.';
        }

        function load(documentType) {
          scope.loading = true;
          scope.error = null;

          return ApiService.getChecklist(documentType)
            .then(function(response) {
              const data = response.data || {};
              scope.documentTypes = data.documentTypes || [];
              scope.documentTypeLabels = data.documentTypeLabels || {};

              if (data.documentType) {
                scope.documentType = data.documentType;
                scope.items = data.items || [];
              } else {
                // First load. Pick a type so the screen has something to be
                // about, rather than showing an empty list for nothing.
                scope.documentType = scope.documentTypes[0] || null;
                if (scope.documentType) return load(scope.documentType);
              }
            })
            .catch(fail)
            .finally(function() {
              scope.loading = false;
              // Every path, including the failed one: the select must show what
              // the screen is actually about, not what was last clicked.
              scope.picker.documentType = scope.documentType;
            });
        }

        scope.chooseDocumentType = function(documentType) {
          if (documentType === scope.documentType) return;
          scope.documentType = documentType;
          load(documentType);
        };

        scope.add = function() {
          const text = (scope.draft.text || '').trim();
          if (!text || scope.saving) return;

          scope.saving = true;
          scope.error = null;

          ApiService.addChecklistItem({
            documentType: scope.documentType,
            text: text,
            severity: scope.draft.severity,
            scopeHint: (scope.draft.scopeHint || '').trim(),
          })
            .then(function(response) {
              scope.items.push(response.data.item);
              scope.draft = { text: '', severity: 'medium', scopeHint: '' };
            })
            .catch(fail)
            .finally(function() {
              scope.saving = false;
            });
        };

        function replace(item) {
          const at = scope.items.findIndex(function(candidate) {
            return candidate.id === item.id;
          });
          if (at !== -1) scope.items[at] = item;
        }

        scope.save = function(item, changes) {
          scope.error = null;
          return ApiService.updateChecklistItem(item.id, changes)
            .then(function(response) {
              replace(response.data.item);
            })
            .catch(fail);
        };

        scope.setSeverity = function(item, severity) {
          if (item.severity === severity) return;
          scope.save(item, { severity: severity });
        };

        scope.toggleEnabled = function(item) {
          scope.save(item, { enabled: !item.enabled });
        };

        scope.rename = function(item) {
          const text = (item.text || '').trim();
          if (!text) return;
          scope.save(item, { text: text });
        };

        scope.remove = function(item) {
          scope.error = null;
          ApiService.deleteChecklistItem(item.id)
            .then(function() {
              scope.items = scope.items.filter(function(candidate) {
                return candidate.id !== item.id;
              });
            })
            .catch(fail);
        };

        scope.acceptSuggestion = function(item) {
          scope.error = null;
          ApiService.acceptChecklistSuggestion(item.id)
            .then(function(response) {
              replace(response.data.item);
            })
            .catch(fail);
        };

        // Reading the wording is a model pass, so it is asked for rather than
        // fired as somebody types — an item half-written is not an item worth
        // an opinion about.
        scope.review = function() {
          if (scope.reviewing || !scope.items.length) return;

          scope.reviewing = true;
          scope.error = null;

          ApiService.reviewChecklist(scope.documentType)
            .then(function(response) {
              scope.items = response.data.items || scope.items;
            })
            .catch(fail)
            .finally(function() {
              scope.reviewing = false;
            });
        };

        // lp-017. Evidence, rather than an opinion about the wording.
        //
        // "Check my wording" reads each item and says whether a clause could
        // fail it. This runs the whole list against a real document and shows
        // what it actually did — which is the only thing that catches the item
        // that reads perfectly and fires on every paragraph.
        //
        // The documents are the ones the server has an outline for, which means
        // the ones somebody has run Checks on. That is a real limit and the
        // panel says so rather than presenting a partial list as the whole.
        scope.documents = [];
        scope.dryRun = null;
        scope.trying = false;
        scope.pickingDocument = false;

        scope.chooseDocument = function() {
          scope.pickingDocument = !scope.pickingDocument;
          if (!scope.pickingDocument || scope.documents.length) return;

          ApiService.checklistDocuments()
            .then(function(response) {
              scope.documents = response.data.documents || [];
            })
            .catch(fail);
        };

        scope.tryAgainst = function(document) {
          if (scope.trying) return;

          scope.trying = true;
          scope.error = null;
          scope.dryRun = null;
          scope.pickingDocument = false;

          ApiService.dryRunChecklist({
            documentType: scope.documentType,
            documentId: document.id,
          })
            .then(function(response) {
              scope.dryRun = {
                document: response.data.document || document,
                items: response.data.items || 0,
                findings: response.data.findings || [],
              };
            })
            .catch(fail)
            .finally(function() {
              scope.trying = false;
            });
        };

        // Which items said something, and which said nothing. Silence is the
        // ordinary answer for a well-written item on a clean document — and it
        // is also what an item that can never fire looks like, so the two are
        // shown side by side rather than only the findings.
        scope.dryRunSilent = function() {
          if (!scope.dryRun) return 0;
          const spoke = new Set(scope.dryRun.findings.map(function(f) {
            return f.item;
          }));
          return scope.dryRun.items - spoke.size;
        };

        scope.closeDryRun = function() {
          scope.dryRun = null;
        };

        // lp-017. Out to Word and back.
        //
        // A template when there is nothing to export yet, which is the same
        // document with the rows blank and the guidance added — one path rather
        // than a second control the customer has to understand.
        scope.exporting = false;

        scope.exportDocument = function() {
          if (scope.exporting) return;

          scope.exporting = true;
          scope.error = null;

          ApiService.downloadChecklistDocument({
            documentType: scope.documentType,
            template: !scope.items.length,
          })
            .then(function(response) {
              const name = filenameFrom(response) || 'Checklist.docx';
              ApiService.downloadFile(
                name,
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                response.data,
              );
            })
            .catch(function() {
              scope.error = 'Could not build the document. Please try again.';
            })
            .finally(function() {
              scope.exporting = false;
            });
        };

        function filenameFrom(response) {
          const disposition =
            response && response.headers && response.headers('content-disposition');
          const match = /filename[^;=\n]*=(?:UTF-8'')?"?([^";\n]*)"?/i.exec(
            disposition || '',
          );
          return match ? decodeURIComponent(match[1]) : null;
        }

        // A PREVIEW, not a write. Import is the moment somebody hands over a
        // list they wrote somewhere else, and what makes it safe to try is that
        // nothing happens until they have seen what we made of it.
        scope.importing = false;
        scope.imported = null;

        scope.importDocument = function(file) {
          if (!file || scope.importing) return;

          scope.importing = true;
          scope.error = null;
          scope.imported = null;

          ApiService.importChecklistDocument({
            documentType: scope.documentType,
            file: file,
          })
            .then(function(response) {
              const found = response.data.items || [];
              scope.imported = {
                source: response.data.source,
                items: found.map(function(item) {
                  return { ...item, keep: true };
                }),
              };
            })
            .catch(fail)
            .finally(function() {
              scope.importing = false;
            });
        };

        scope.discardImport = function() {
          scope.imported = null;
        };

        // Added one at a time through the same endpoint a hand-typed item goes
        // through. An import that took its own path into the database would be
        // a second way for an item to exist, and the first thing to diverge.
        scope.keepImported = function() {
          if (!scope.imported || scope.saving) return;

          const keeping = scope.imported.items.filter(function(item) {
            return item.keep;
          });
          if (!keeping.length) {
            scope.imported = null;
            return;
          }

          scope.saving = true;
          scope.error = null;

          const added = keeping.map(function(item) {
            return ApiService.addChecklistItem({
              documentType: scope.documentType,
              text: item.text,
              scopeHint: item.scopeHint,
              severity: item.severity,
              // A retired item comes back retired. Without this, exporting a
              // list and importing it again switched every one of them on.
              enabled: item.enabled !== false,
            }).then(function(response) {
              scope.items.push(response.data.item);
            });
          });

          Promise.all(added)
            .then(function() {
              scope.imported = null;
            })
            .catch(fail)
            .finally(function() {
              scope.saving = false;
              scope.$applyAsync();
            });
        };

        scope.labelFor = function(documentType) {
          return scope.documentTypeLabels[documentType] || documentType;
        };

        load(null);
      },
    };
  },
]);
